/**
 * SubtitleView — View component for the subtitle content type.
 *
 * Shows subtitle metadata (language, task, flags) and a structured cue preview.
 * Provides an "Edit" link to the subtitle editor.
 * This is what users see when they navigate to a subtitle URL.
 */
import React, { useEffect, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  Container as SemanticContainer,
  Header,
  Label,
} from 'semantic-ui-react';
import config from '@plone/volto/registry';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import Api from '@plone/volto/helpers/Api/Api';
import { getLanguageName } from 'volto-base-addon/helpers/languageOptions';
import { parseVTT, formatTimestamp } from 'volto-base-addon/helpers/vttParser';
import { formatFileSize } from 'volto-base-addon/helpers/fileSize';

const messages = defineMessages({
  language: {
    id: 'SubtitleView.language',
    defaultMessage: 'Language',
  },
  task: {
    id: 'SubtitleView.task',
    defaultMessage: 'Task',
  },
  taskTranscribe: {
    id: 'SubtitleView.taskTranscribe',
    defaultMessage: 'Transcription',
  },
  taskTranslate: {
    id: 'SubtitleView.taskTranslate',
    defaultMessage: 'Translation',
  },
  diarized: {
    id: 'SubtitleView.diarized',
    defaultMessage: 'Diarized',
  },
  fillers: {
    id: 'SubtitleView.fillers',
    defaultMessage: 'Fillers',
  },
  translatedFrom: {
    id: 'SubtitleView.translatedFrom',
    defaultMessage: 'Translated from',
  },
  preview: {
    id: 'SubtitleView.preview',
    defaultMessage: 'Preview',
  },
  noContent: {
    id: 'SubtitleView.noContent',
    defaultMessage: 'No subtitle content yet. Click Edit to add content.',
  },
  backToMediaPage: {
    id: 'SubtitleView.backToMediaPage',
    defaultMessage: 'Back to media page',
  },
  cueCount: {
    id: 'SubtitleView.cueCount',
    defaultMessage: '{count, plural, one {# cue} other {# cues}}',
  },
  fileSize: {
    id: 'SubtitleView.fileSize',
    defaultMessage: 'Size',
  },
  loading: {
    id: 'SubtitleView.loading',
    defaultMessage: 'Loading subtitle content…',
  },
  timestamp: {
    id: 'SubtitleView.timestamp',
    defaultMessage: 'Timestamp',
  },
  moreCues: {
    id: 'SubtitleView.moreCues',
    defaultMessage: '… {count, plural, one {# more cue} other {# more cues}}',
  },
});

const TASK_LABELS = {
  transcribe: 'taskTranscribe',
  translate: 'taskTranslate',
};

/**
 * Translate a raw task string like "transcribe" into a localized label.
 */
function getTaskLabel(task, intl) {
  const msgKey = TASK_LABELS[task];
  if (msgKey && messages[msgKey]) {
    return intl.formatMessage(messages[msgKey]);
  }
  return task;
}

const MAX_PREVIEW_CUES = 20;

const SubtitleView = ({ content }) => {
  const intl = useIntl();
  // The registered Container, as MediaPageView uses: Semantic's own is capped
  // at 870px by the theme on most desktop widths.
  const Container =
    config.getComponent({ name: 'Container' }).component || SemanticContainer;
  const [vttContent, setVttContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const contentPath = flattenToAppURL(content['@id']);
  const parentPath = content.parent
    ? flattenToAppURL(content.parent['@id'])
    : null;

  useEffect(() => {
    const api = new Api();
    api
      .get(`${contentPath}/@subtitle-content`)
      .then((resp) => {
        setVttContent(resp.content || '');
        setLoading(false);
      })
      .catch(() => {
        setVttContent(null);
        setLoading(false);
      });
  }, [contentPath]);

  const rawLang = content.subtitle_language;
  const lang =
    typeof rawLang === 'object' && rawLang?.token ? rawLang.token : rawLang;
  const langName = lang ? getLanguageName(lang, intl.locale) : null;
  const cues = vttContent ? parseVTT(vttContent) : [];
  const previewCues = cues.slice(0, MAX_PREVIEW_CUES);
  const hasMoreCues = cues.length > MAX_PREVIEW_CUES;

  const rawTask = content.subtitle_task;
  const task =
    typeof rawTask === 'object' && rawTask?.token ? rawTask.token : rawTask;
  const rawTranslatedFrom = content.subtitle_translated_from;
  const translatedFrom =
    typeof rawTranslatedFrom === 'object' && rawTranslatedFrom?.token
      ? rawTranslatedFrom.token
      : rawTranslatedFrom;

  const hasMetadata =
    langName ||
    task ||
    content.subtitle_diarized ||
    content.subtitle_fillers ||
    translatedFrom ||
    cues.length > 0 ||
    content.file_size;

  return (
    <Container id="page-document" className="subtitle-view default">
      <Helmet title={content.title} />

      {parentPath && (
        <Link to={parentPath} className="subtitle-back-link">
          ← {intl.formatMessage(messages.backToMediaPage)}
        </Link>
      )}

      <Header as="h1" className="documentFirstHeading">
        {content.title}
      </Header>

      {hasMetadata && (
        <div className="subtitle-metadata">
          <div className="subtitle-meta-grid">
            {langName && (
              <div className="subtitle-meta-item">
                <strong>{intl.formatMessage(messages.language)}:</strong>{' '}
                {langName} ({lang})
              </div>
            )}
            {task && (
              <div className="subtitle-meta-item">
                <strong>{intl.formatMessage(messages.task)}:</strong>{' '}
                {getTaskLabel(task, intl)}
              </div>
            )}
            {content.subtitle_diarized && (
              <div className="subtitle-meta-item">
                <Label size="tiny" color="blue">
                  {intl.formatMessage(messages.diarized)}
                </Label>
              </div>
            )}
            {content.subtitle_fillers && (
              <div className="subtitle-meta-item">
                <Label size="tiny" color="orange">
                  {intl.formatMessage(messages.fillers)}
                </Label>
              </div>
            )}
            {translatedFrom && (
              <div className="subtitle-meta-item">
                <strong>{intl.formatMessage(messages.translatedFrom)}:</strong>{' '}
                {getLanguageName(translatedFrom, intl.locale)}
              </div>
            )}
            {cues.length > 0 && (
              <div className="subtitle-meta-item">
                <strong>
                  {intl.formatMessage(messages.cueCount, {
                    count: cues.length,
                  })}
                </strong>
              </div>
            )}
            {content.file_size && (
              <div className="subtitle-meta-item">
                <strong>{intl.formatMessage(messages.fileSize)}:</strong>{' '}
                {formatFileSize(content.file_size)}
              </div>
            )}
          </div>
        </div>
      )}

      {loading && (
        <p className="subtitle-loading" aria-live="polite">
          {intl.formatMessage(messages.loading)}
        </p>
      )}

      {!loading && cues.length > 0 && (
        <section
          className="subtitle-preview"
          aria-label={intl.formatMessage(messages.preview)}
        >
          <Header as="h3">{intl.formatMessage(messages.preview)}</Header>
          <ol className="subtitle-cue-preview-list">
            {previewCues.map((cue, i) => (
              <li key={i} className="subtitle-cue-preview-row">
                <span
                  className="subtitle-cue-preview-time"
                  aria-label={intl.formatMessage(messages.timestamp)}
                >
                  {formatTimestamp(cue.startTime)}
                </span>
                <span className="subtitle-cue-preview-text">{cue.text}</span>
              </li>
            ))}
          </ol>
          {hasMoreCues && (
            <p className="subtitle-preview-more">
              {intl.formatMessage(messages.moreCues, {
                count: cues.length - MAX_PREVIEW_CUES,
              })}
            </p>
          )}
        </section>
      )}

      {!loading && cues.length === 0 && (
        <p className="subtitle-empty-note">
          {intl.formatMessage(messages.noContent)}
        </p>
      )}
    </Container>
  );
};

export default SubtitleView;
